import React, { type ReactNode } from 'react';

import { Alert, Button, CircularProgress, Link } from '@mui/material';

import ErrorDisplay from '../ErrorDisplay';

interface QueuedReportControlsProps {
  submitLabel: string;
  isRunning: boolean;
  hasResults: boolean;
  completed: boolean;
  error: string | null;
  downloadUrl: string | null;
  errorContent?: ReactNode;
  lookingUpLabel?: string;
  generatingLabel?: string;
}

/** Common in-page feedback for a report that is queued, emailed, and downloadable when ready. */
export default function QueuedReportControls({
  submitLabel,
  isRunning,
  hasResults,
  completed,
  error,
  downloadUrl,
  errorContent,
  lookingUpLabel = 'Preparing report…',
  generatingLabel = 'Generating file…',
}: QueuedReportControlsProps) {
  return (
    <>
      <Button
        type="submit"
        variant="contained"
        disabled={isRunning}
        startIcon={isRunning ? <CircularProgress size={16} color="inherit" /> : undefined}
      >
        {isRunning ? (hasResults ? generatingLabel : lookingUpLabel) : submitLabel}
      </Button>

      {isRunning && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {hasResults
            ? 'Report generating and will be emailed when complete or please wait to download here.'
            : lookingUpLabel}
        </Alert>
      )}

      {error && (errorContent ?? <ErrorDisplay message={error} />)}

      {error && downloadUrl && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          <p>The report is ready, but the email could not be sent.</p>
          <p>
            <Link href={downloadUrl} target="_blank" rel="noopener noreferrer">
              Download now
            </Link>
          </p>
        </Alert>
      )}

      {completed && (
        <Alert severity="success">
          <p>Your report has been emailed.</p>
          {downloadUrl && (
            <p>
              <Link href={downloadUrl} target="_blank" rel="noopener noreferrer">
                Download now
              </Link>
            </p>
          )}
        </Alert>
      )}
    </>
  );
}
